import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Combobox, Dialog, EmptyState, Inline, Stack, t, Text, TextField, toast, Tooltip } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { ListTable } from './ListTable';
import { api, ApiFailure, unwrap } from '../lib/api';
import { relativeTime, shortVersion } from '../lib/format';

type Release = maintenance.components['schemas']['InstallationRelease'];
type Environment = maintenance.operations['getEnvironment']['responses']['200']['content']['application/json'];

const STATE_TONE: Record<Release['state'], 'pos' | 'warn' | 'neg' | 'neutral' | 'info'> = {
  running: 'info', succeeded: 'pos', rolled_back_to: 'pos', failed_rolled_back: 'warn', failed_rollback_proposed: 'neg', failed_rollback_failed: 'neg', failed: 'neg',
};
const who = (ref: string) => ref.replace(/^user:default\//, '');

export const releasesKey = (id: string) => ['environment-releases', id];

/**
 * SPEC-034 § 8.4 and § 11.2 (lot 1.5, slice 2a): the releases of an installed
 * environment, « Déployer une version » (the version published and attested
 * for the head of a branch) and « Revenir à cette version » (through the
 * engine, without GitHub: a reason and a recent strong authentication).
 */
export function EnvironmentVersions({ env, deploying, onDeployClose, onStepUp }: {
  env: Environment; deploying: boolean; onDeployClose: () => void; onStepUp: () => void;
}) {
  const queryClient = useQueryClient();
  // The branches of the repository, read when « Déployer une version » opens (the analysis already reads them).
  const branches = useQuery({
    queryKey: ['deploy-analysis-branches', env.application, env.ref],
    queryFn: async () => unwrap(await api.maintenance.GET('/applications/{application}/deploy-analysis', { params: { path: { application: env.application }, query: { ref: env.ref } } })).branches,
    enabled: deploying,
    retry: false,
    staleTime: 60_000,
  });
  const list = useQuery({
    queryKey: releasesKey(env.id),
    queryFn: async () => unwrap(await api.maintenance.GET('/installations/{id}/releases', { params: { path: { id: env.id } } })),
    refetchInterval: q => (q.state.data?.items.some(r => r.state === 'running') ? 3_000 : 30_000),
  });
  const [ref, setRef] = useState(env.ref);
  const [back, setBack] = useState<Release | null>(null);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: releasesKey(env.id) });
    void queryClient.invalidateQueries({ queryKey: ['environment', env.deployment] });
  };
  const send = async (action: () => Promise<unknown>, done: () => void, title: string | ((result: unknown) => string)) => {
    setSending(true);
    try {
      const result = await action();
      done();
      toast({ tone: 'info', title: typeof title === 'string' ? title : title(result) });
      refresh();
    } catch (err) {
      if (err instanceof ApiFailure && err.body.code === 'MFA_STEP_UP_REQUIRED') { done(); onStepUp(); return; }
      toast({ tone: 'neg', title: err instanceof ApiFailure ? err.body.message : String(err), description: err instanceof ApiFailure ? err.body.hint : undefined });
    } finally {
      setSending(false);
    }
  };

  const items = list.data?.items ?? [];
  const running = items.find(r => r.state === 'running');
  const proposed = items.find(r => r.state === 'failed_rollback_proposed');

  return (
    <Stack gap={16}>
      {running && <Alert tone="info" title={t('environment.versions.running', { version: shortVersion(running.version) })}>{t('environment.versions.runningBody')}</Alert>}
      {proposed && !running && <Alert tone="neg" title={t('environment.versions.proposedTitle', { version: shortVersion(proposed.version) })}>{proposed.failure ?? t('environment.versions.proposedBody')}</Alert>}
      {/* D120: every version, searched, filtered and paged by the server; the newest first. */}
      <ListTable<Release>
        label={t('environment.versions.table')}
        queryKey={[...releasesKey(env.id), 'list']}
        fetch={async query => unwrap(await api.maintenance.GET('/installations/{id}/releases', { params: { path: { id: env.id }, query: query as never } }))}
        rowKey="id"
        defaultSort={{ key: 'createdAt', dir: 'desc' }}
        search={t('environment.versions.search')}
        facets={[
          { key: 'state', label: t('environment.versions.result'), valueLabel: v => t(`environment.versions.states.${v}`) },
          { key: 'kind', label: t('environment.versions.kind'), valueLabel: v => t(`environment.versions.kinds.${v}`) },
        ]}
        refetchInterval={running ? 3_000 : 30_000}
        empty={<EmptyState framed={false} icon="history" title={t('environment.versions.empty')} />}
        columns={[
          {
            key: 'version', label: t('environment.versions.version'), grow: true,
            render: (r: Release) => (
              <Inline gap={8}>
                <Tooltip content={r.version}><Text variant="code">{shortVersion(r.version)}</Text></Tooltip>
                {r.current && <Badge tone="brand" size="sm">{t('environment.versions.current')}</Badge>}
              </Inline>
            ),
          },
          { key: 'kind', label: t('environment.versions.kind'), sortable: false, render: (r: Release) => t(`environment.versions.kinds.${r.kind}`) },
          {
            key: 'state', label: t('environment.versions.result'), sortable: false,
            render: (r: Release) => (
              <Stack gap={4}>
                <Badge tone={STATE_TONE[r.state]} size="sm">{t(`environment.versions.states.${r.state}`)}</Badge>
                {r.failure && <Text variant="caption" tone="mute">{r.failure}</Text>}
              </Stack>
            ),
          },
          { key: 'createdAt', label: t('environment.versions.when'), render: (r: Release) => `${relativeTime(r.createdAt)} · ${who(r.createdBy)}` },
          ...(env.allowed.rollback ? [{
            key: 'actions', label: '', pinned: 'right' as const, sortable: false,
            render: (r: Release) => ((r.state === 'succeeded' || r.state === 'rolled_back_to') && !r.current && !running
              ? <Button variant="ghost" size="sm" icon="undo" onClick={() => { setReason(''); setBack(r); }}>{t('environment.versions.back')}</Button>
              : null),
          }] : []),
        ]}
      />

      <Dialog
        open={deploying}
        onClose={onDeployClose}
        title={t('environment.versions.deployTitle', { deployment: env.deployment })}
        description={t('environment.versions.deployBody')}
        confirmLabel={t('environment.versions.deployConfirm')}
        confirmDisabled={sending || !ref.trim()}
        onConfirm={() => void send(
          async () => unwrap(await api.maintenance.POST('/installations/{id}/releases', { params: { path: { id: env.id } }, body: { ref: ref.trim() } })),
          onDeployClose,
          // D99: the repository's deploy workflow was asked; the version appears here once it calls the platform.
          (r: unknown) => t((r as { dispatched?: boolean } | null)?.dispatched ? 'environment.versions.deployAsked' : 'environment.versions.deployStarted', { ref: ref.trim() }),
        )}
      >
        {/* The repository's branches, as in the assistant; a tag or a commit is typed and created in the list (DSM Combobox creatable). */}
        <Combobox id="release-ref" label={t('environment.versions.branch')} hint={t('environment.versions.branchHint')} searchable creatable
          options={[...new Set([...(ref ? [ref] : []), ...(branches.data ?? [env.ref])])].map(b => ({ value: b, label: b }))}
          value={ref || null} onChange={(v: string | null) => setRef(v ?? '')} onCreate={(label: string) => setRef(label.trim())} />
      </Dialog>

      <Dialog
        open={back !== null}
        onClose={() => setBack(null)}
        title={t('environment.versions.backTitle', { version: shortVersion(back?.version) })}
        description={t('environment.versions.backBody')}
        confirmLabel={t('environment.versions.backConfirm')}
        confirmDisabled={sending || reason.trim().length < 3}
        onConfirm={() => back && void send(
          async () => unwrap(await api.maintenance.POST('/installations/{id}/rollback', { params: { path: { id: env.id } }, body: { to: back.id, reason: reason.trim() } })),
          () => setBack(null), t('environment.versions.backStarted', { version: shortVersion(back.version) }),
        )}
      >
        <TextField id="rollback-reason" label={t('environment.versions.reason')} value={reason} onChange={e => setReason(e.target.value)} />
      </Dialog>
    </Stack>
  );
}
